import React, { Fragment, useEffect, useState } from "react";
import {
  isEmpty,
  pick,
  map,
  path,
  pipe,
  prop,
  sortBy,
  toLower,
  filter,
  ifElse,
  propEq,
  reject,
  identity,
} from "ramda";
import { useSelector, useDispatch } from "react-redux";
import {
  getCountries,
  getLibraries,
  getShowLocalLibraryOther,
  getStepErrors,
  getStepFields,
  getSubdivisions,
  isCountryEmpty,
  isLocalLibraryOtherEmpty,
  getSubmitterIsCreator,
  getActiveStep,
  getProjectContributorRoles,
  getDefaultLibraryId,
  getProjectLibraries,
  getFieldOptions,
} from "common/selectors";
import { makeStyles } from "@material-ui/core/styles";
import * as basicDetailsActions from "basic-details/basicDetailsActions";
import * as introActions from "intro/introActions";
import { firstError, hasError } from "common/validation";
import { isBlank, notBlank } from "common/general";
import { useTranslation } from "react-i18next";
import { useIsQatarProject } from "../../common/projectUtils";
import { getFieldOptionLabel } from "../../common/stringFormat";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import AutocompleteInputField from "../../general-components/AutocompleteInputField";
import CustomizableTextField from "../../general-components/CustomizableTextField";
import HidableGridItem from "../../general-components/HidableGridItem";
import IntlAutocomplete from "../../general-components/IntlAutocomplete";
import IntlTextField from "../../general-components/IntlTextField";

const useStyles = makeStyles(theme => ({
  addLibraryRow: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    minWidth: 225,
    fontSize: ".75rem",
  },

  sectionHeader: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  submitterBlock: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
}));

const PersonalDetailsForm = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const activeStep = useSelector(getActiveStep);
  const errors = useSelector(getStepErrors(activeStep));
  const fields = useSelector(getStepFields(activeStep));
  const defaultLibraryId = useSelector(getDefaultLibraryId);
  const isQatarProject = useIsQatarProject();

  const projectLibraries = useSelector(getProjectLibraries);
  const [projectLibraryOptions, setProjectLibraryOptions] = useState([]);
  useEffect(() => {
    const options = pipe(
      sortBy(prop("libraryName")),
      map(prop("libraryName")),
    )(projectLibraries);
    setProjectLibraryOptions(options);
  }, [projectLibraries]);

  const countries = useSelector(getCountries);
  const [countryOptions, setCountryOptions] = useState([]);
  useEffect(() => {
    const options = pipe(
      sortBy(prop("name")),
      sortBy(prop("displayOrder")),
      map(pick(["code", "name"])),
    )(countries);

    // Remove Israel for Qatar Palestine projects
    setCountryOptions(
      ifElse(
        () => isQatarProject,
        reject(propEq("code", "IL")),
        identity,
      )(options),
    );
  }, [countries, isQatarProject]);

  const subdivisions = useSelector(getSubdivisions);
  const [subdivisionOptions, setSubdivisionOptions] = useState([]);
  useEffect(() => {
    const options = pipe(
      sortBy(prop("name")),
      map(pick(["code", "name"])),
    )(subdivisions);
    setSubdivisionOptions(options);
  }, [subdivisions]);

  const libraries = useSelector(getLibraries);
  const [libraryOptions, setLibraryOptions] = useState([]);
  useEffect(() => {
    if (isEmpty(projectLibraries)) {
      const options = pipe(
        sortBy(prop("organizationName")),
        filter(
          lib =>
            isBlank(lib.location) ||
            isBlank(fields.region) ||
            lib.location.administrativeArea === fields.region.name ||
            fields.region.name.includes(lib.location.administrativeArea) ||
            (defaultLibraryId && lib.organizationId === defaultLibraryId),
        ),
        map(pick(["organizationId", "organizationName"])),
      )(libraries);
      setLibraryOptions(options);
    }
  }, [projectLibraries, libraries, fields, defaultLibraryId]);

  const roles = useSelector(getProjectContributorRoles);
  const [roleOptions, setRoleOptions] = useState([]);
  useEffect(() => {
    const options = pipe(sortBy(prop("name")))(roles);
    setRoleOptions(options);
  }, [roles]);

  const countryEmpty = useSelector(isCountryEmpty);
  const showLocalLibraryOther = useSelector(getShowLocalLibraryOther);
  const localLibraryOtherEmpty = useSelector(isLocalLibraryOtherEmpty);
  const isSubmitterCreator = useSelector(getSubmitterIsCreator);

  const fieldOptions = useSelector(getFieldOptions);
  const [showAddLibrary, setShowAddLibrary] = useState(true);
  useEffect(() => {
    if (fieldOptions["local_library_other"]) {
      setShowAddLibrary(!fieldOptions["local_library_other"].hidden);
    } else {
      setShowAddLibrary(true);
    }
  }, [fieldOptions]);

  const actions = isSubmitterCreator ? basicDetailsActions : introActions;

  const dispatch = useDispatch();
  const onBlur = (field, value) => () =>
    dispatch({ type: actions.FIELD_BLURRED, field, value });
  const onCountryChange = (event, country) =>
    dispatch({ type: actions.COUNTRY_CHANGED, country });
  const onLocalLibraryChange = (event, library) =>
    dispatch({ type: actions.LOCAL_LIBRARY_CHANGED, library });
  const onProjectLibraryChange = (event, library) =>
    dispatch({
      type: actions.FIELD_CHANGED,
      path: ["localLibraryOther"],
      value: library,
    });
  const onRoleChange = (event, role) =>
    dispatch({ type: actions.ROLE_CHANGED, role });
  const onFieldChange = path => event => {
    const value = event.target.value;
    dispatch({ type: actions.FIELD_CHANGED, path, value });
  };
  const onRegionChange = (event, region) =>
    dispatch({ type: actions.REGION_CHANGED, region });

  const onShowLibrariesList = () =>
    dispatch({
      type: basicDetailsActions.SET_SHOW_LOCAL_LIBRARY_OTHER,
      show: false,
    });
  const onShowLocalLibraryOther = () =>
    dispatch({
      type: basicDetailsActions.SET_SHOW_LOCAL_LIBRARY_OTHER,
      show: true,
    });

  return (
    <form autoComplete="none">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <CustomizableTextField
              required
              id="email"
              fieldOption="email"
              xs={12}
              sm={4}
              defaultLabel={t("personal_details.email", "Email")}
              value={fields.email || ""}
              onChange={onFieldChange(["email"])}
              error={hasError(errors, "email")}
              helperText={t(firstError(errors, "email"))}
              onBlur={onBlur("email", fields.email)}
              inputProps={{ maxLength: 100, id: "email" }}
            />
          </Grid>
        </Grid>

        {isSubmitterCreator && (
          <CustomizableTextField
            id="prefix"
            fieldOption="prefix"
            xs={6}
            sm={4}
            defaultLabel={t("personal_details.prefix", "Prefix")}
            value={fields.prefix || ""}
            onChange={onFieldChange(["prefix"])}
            error={hasError(errors, "prefix")}
            helperText={t(firstError(errors, "prefix"))}
            onBlur={onBlur("prefix", fields.prefix)}
            inputProps={{ maxLength: 50, id: "prefix" }}
          />
        )}

        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <CustomizableTextField
              required
              id="first-name"
              fieldOption="first_name"
              xs={isSubmitterCreator ? 12 : 6}
              sm={4}
              defaultLabel={t("personal_details.first_name", "First name")}
              value={fields.firstName || ""}
              onChange={onFieldChange(["firstName"])}
              error={hasError(errors, "firstName")}
              helperText={t(firstError(errors, "firstName"))}
              onBlur={onBlur("firstName", fields.firstName)}
              inputProps={{ maxLength: 100, id: "first-name" }}
            />

            {isSubmitterCreator && (
              <CustomizableTextField
                id="middle-name"
                fieldOption="middle_name"
                xs={12}
                sm={4}
                defaultLabel={t("personal_details.middle_name", "Middle name")}
                value={fields.middleName || ""}
                onChange={onFieldChange(["middleName"])}
                error={hasError(errors, "middleName")}
                helperText={t(firstError(errors, "middleName"))}
                onBlur={onBlur("middleName", fields.middleName)}
                inputProps={{ maxLength: 100, id: "middle-name" }}
              />
            )}

            <CustomizableTextField
              required
              id="last-name"
              fieldOption="last_name"
              xs={isSubmitterCreator ? 12 : 6}
              sm={4}
              defaultLabel={t("personal_details.last_name", "Last name")}
              value={fields.lastName || ""}
              onChange={onFieldChange(["lastName"])}
              error={hasError(errors, "lastName")}
              helperText={t(firstError(errors, "lastName"))}
              onBlur={onBlur("lastName", fields.lastName)}
              inputProps={{ maxLength: 100, id: "last-name" }}
            />
          </Grid>
        </Grid>

        {!isSubmitterCreator && (
          <Grid item xs={12} className={classes.submitterBlock}>
            <Typography variant="h2">
              {t("intro_about_creator", "Now, about the creator.")}
              <Typography variant="body2">
                {t(
                  "intro_about_creator_sub",
                  "Please fill in some details about the creator.",
                )}
              </Typography>
            </Typography>
          </Grid>
        )}

        {isSubmitterCreator && (
          <CustomizableTextField
            id="suffix"
            fieldOption="suffix"
            xs={6}
            sm={4}
            defaultLabel={t("personal_details.suffix", "Suffix")}
            value={fields.suffix || ""}
            onChange={onFieldChange(["suffix"])}
            error={hasError(errors, "suffix")}
            helperText={t(firstError(errors, "suffix"))}
            onBlur={onBlur("suffix", fields.suffix)}
            inputProps={{ maxLength: 100, id: "suffix" }}
          />
        )}

        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <CustomizableTextField
              id="locality"
              fieldOption="locality"
              xs={12}
              sm={4}
              defaultLabel={t("personal_details.city", "City")}
              value={fields.locality || ""}
              onChange={onFieldChange(["locality"])}
              error={hasError(errors, "locality")}
              helperText={t(firstError(errors, "locality"))}
              onBlur={onBlur("locality", fields.locality)}
              inputProps={{ maxLength: 64, id: "locality" }}
            />

            <HidableGridItem fieldOption="country" xs={6} sm={4}>
              <IntlAutocomplete
                id="country"
                disableClearable
                autoSelect
                options={countryOptions}
                value={path(["country"], fields) || ""}
                getOptionLabel={country =>
                  notBlank(country)
                    ? t(`countries.${country.code}`, country.name)
                    : ""
                }
                getOptionSelected={(opt, val) =>
                  isBlank(val) || opt.code === val.code
                }
                onChange={onCountryChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = "country";
                  return (
                    <AutocompleteInputField
                      params={params}
                      fieldOption="country"
                      onBlur={onBlur("country", path(["country"], fields))}
                      error={hasError(errors, "country")}
                      helperText={t(firstError(errors, "country"))}
                      name={"country-" + Math.random()}
                      defaultLabel={t("personal_details.country", "Country")}
                    />
                  );
                }}
              />
            </HidableGridItem>

            <HidableGridItem fieldOption="region" xs={6} sm={4}>
              <IntlAutocomplete
                id="region"
                disableClearable
                autoSelect
                disabled={countryEmpty || isEmpty(subdivisionOptions)}
                options={subdivisionOptions}
                value={path(["region"], fields) || ""}
                getOptionLabel={region => (notBlank(region) ? region.name : "")}
                getOptionSelected={(opt, val) =>
                  isBlank(val) || opt.code === val.code
                }
                onChange={onRegionChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = "region";
                  return (
                    <AutocompleteInputField
                      params={params}
                      fieldOption="region"
                      onBlur={onBlur("region", path(["region"], fields))}
                      error={hasError(errors, "region")}
                      helperText={t(firstError(errors, "region"))}
                      name={"region"}
                      defaultLabel={t(
                        "personal_details.state",
                        "State or province",
                      )}
                    />
                  );
                }}
              />
            </HidableGridItem>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <CustomizableTextField
              id={getFieldOptionLabel("freeform_personal_data", fieldOptions)}
              fieldOption="freeform_personal_data"
              xs={6}
              sm={4}
              defaultLabel={t(
                "personal_details.freeform_data",
                "Freeform data",
              )}
              hideByDefault
              value={fields.freeformPersonalData || ""}
              onChange={onFieldChange(["freeformPersonalData"])}
              error={hasError(errors, "freeformPersonalData")}
              helperText={t(firstError(errors, "freeformPersonalData"), {
                freeformPersonalDataLabel: notBlank(
                  fieldOptions["freeform_personal_data"],
                )
                  ? fieldOptions["freeform_personal_data"].fieldLabel
                  : "Personal Data",
              })}
              onBlur={onBlur(
                "freeformPersonalData",
                fields.freeformPersonalData,
              )}
              inputProps={{
                maxLength: 512,
                id: getFieldOptionLabel("freeform_personal_data", fieldOptions),
              }}
            />

            <CustomizableTextField
              id={getFieldOptionLabel("freeform_personal_data_2", fieldOptions)}
              fieldOption="freeform_personal_data_2"
              xs={6}
              sm={4}
              defaultLabel={t(
                "personal_details.freeform_data_2",
                "Freeform data",
              )}
              hideByDefault
              value={fields.freeformPersonalData2 || ""}
              onChange={onFieldChange(["freeformPersonalData2"])}
              error={hasError(errors, "freeformPersonalData2")}
              helperText={t(firstError(errors, "freeformPersonalData2"), {
                freeformPersonalData2Label: notBlank(
                  fieldOptions["freeform_personal_data_2"],
                )
                  ? fieldOptions["freeform_personal_data_2"].fieldLabel
                  : "Personal Data 2",
              })}
              onBlur={onBlur(
                "freeformPersonalData2",
                fields.freeformPersonalData2,
              )}
              inputProps={{
                maxLength: 512,
                id: getFieldOptionLabel(
                  "freeform_personal_data_2",
                  fieldOptions,
                ),
              }}
            />

            <HidableGridItem fieldOption="local_library" xs={6} sm={8} md={6}>
              {(localLibraryOtherEmpty || notBlank(projectLibraryOptions)) &&
                !showLocalLibraryOther && (
                  <Fragment>
                    {isBlank(projectLibraries) ? (
                      <div>
                        <IntlAutocomplete
                          autoSelect
                          id="other-local-libraries"
                          options={libraryOptions}
                          value={fields.localLibrary || ""}
                          getOptionLabel={org =>
                            notBlank(org) ? org.organizationName : ""
                          }
                          getOptionSelected={(org, val) =>
                            isBlank(val) ||
                            org.organizationId === val.organizationId
                          }
                          onChange={onLocalLibraryChange}
                          renderInput={params => {
                            params.inputProps["aria-autocomplete"] = "none";
                            params.inputProps["id"] = "other-local-libraries";
                            return (
                              <IntlTextField
                                {...params}
                                onBlur={onBlur(
                                  "localLibrary",
                                  path(["localLibrary"], fields),
                                )}
                                error={hasError(errors, "localLibrary")}
                                helperText={t(
                                  firstError(errors, "localLibrary"),
                                )}
                                name={"localLibrary=" + Math.random()}
                                variant="standard"
                                label={t(
                                  "personal_details.local_library",
                                  "Local library",
                                )}
                                fullWidth
                              />
                            );
                          }}
                        />
                      </div>
                    ) : (
                      <div>
                        <IntlAutocomplete
                          autoSelect
                          id="other-local-libraries"
                          options={projectLibraryOptions}
                          value={fields.localLibraryOther}
                          onChange={onProjectLibraryChange}
                          renderInput={params => {
                            params.inputProps["aria-autocomplete"] = "none";
                            params.inputProps["id"] = "other-local-libraries";
                            return (
                              <IntlTextField
                                {...params}
                                onBlur={onBlur(
                                  "localLibraryOther",
                                  path(["localLibraryOther"], fields),
                                )}
                                error={hasError(errors, "localLibraryOther")}
                                helperText={t(
                                  t(firstError(errors, "localLibraryOther")),
                                )}
                                name={"localLibraryOther=" + Math.random()}
                                variant="standard"
                                label={t(
                                  "personal_details.local_library",
                                  "Local library",
                                )}
                                fullWidth
                              />
                            );
                          }}
                        />
                      </div>
                    )}

                    {showAddLibrary && (
                      <div className={classes.addLibraryRow}>
                        <span>
                          {t(
                            "personal_details.dont_see_library",
                            "Don't see your library?",
                          )}
                        </span>
                        <Button
                          variant="text"
                          onClick={onShowLocalLibraryOther}
                        >
                          {t(
                            "personal_details.add_to_list",
                            "Add it to the list.",
                          )}
                        </Button>
                      </div>
                    )}
                  </Fragment>
                )}

              {((isBlank(projectLibraryOptions) && !localLibraryOtherEmpty) ||
                showLocalLibraryOther) && (
                <Fragment>
                  <div className={classes.row}>
                    <CustomizableTextField
                      id="other-local-library"
                      fieldOption="localLibraryOther"
                      defaultLabel={t(
                        "personal_details.enter_local_library",
                        "Enter the name of your local library",
                      )}
                      value={fields.localLibraryOther || ""}
                      onChange={onFieldChange(["localLibraryOther"])}
                      error={hasError(errors, "localLibraryOther")}
                      helperText={t(firstError(errors, "localLibraryOther"))}
                      onBlur={onBlur(
                        "localLibraryOther",
                        fields.localLibraryOther,
                      )}
                      inputProps={{ maxLength: 255, id: "other-local-library" }}
                    />
                  </div>
                  <div className={classes.addLibraryRow}>
                    <Button variant="text" onClick={onShowLibrariesList}>
                      {t(
                        "personal_details.show_list",
                        "Show me that list of libraries again",
                      )}
                    </Button>
                  </div>
                </Fragment>
              )}
            </HidableGridItem>
          </Grid>
        </Grid>

        {/* {isSubmitterCreator && (
          <CustomizableTextField
            id="x.com-handle"
            fieldOption="twitter_handle"
            xs={6}
            sm={6}
            md={4}
            defaultLabel={t(
              "personal_details.twitter_handle",
              "X handle (optional)",
            )}
            value={fields.twitterHandle || ""}
            onChange={onFieldChange(["twitterHandle"])}
            error={hasError(errors, "twitterHandle")}
            helperText={t(firstError(errors, "twitterHandle"))}
            onBlur={onBlur("twitterHandle", fields.twitterHandle)}
            inputProps={{ maxLength: 30, id: "x.com-handle" }}
          />
        )} */}

        {isSubmitterCreator && (
          <CustomizableTextField
            id="website"
            fieldOption="website"
            xs={6}
            sm={6}
            md={4}
            defaultLabel={t(
              "personal_details.website_url",
              "Website URL (optional)",
            )}
            value={fields.website || ""}
            onChange={onFieldChange(["website"])}
            error={hasError(errors, "website")}
            helperText={t(firstError(errors, "website"))}
            onBlur={onBlur("website", fields.website)}
            inputProps={{ maxLength: 2048, id: "website" }}
          />
        )}

        {isSubmitterCreator && (
          <Grid item xs={12} sm={12}>
            <Grid container>
              <Grid item xs={12} sm={12}>
                <div className={classes.sectionHeader}>
                  <Typography variant="h2">
                    {t("personal_details_select_role")}{" "}
                    <Typography variant="body2">
                      {t("personal_details_select_role_sub")}
                    </Typography>
                  </Typography>
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <IntlAutocomplete
                  disableClearable
                  id="role"
                  options={roleOptions}
                  value={path(["role"], fields) || ""}
                  getOptionLabel={role =>
                    // We might move translations to stip in the future
                    // Base translation on role.name for now
                    // If key doesn't exist, use role.display as fallback
                    notBlank(role)
                      ? t(
                          `personal_details.roles.${toLower(role.name)}`,
                          role.display,
                        )
                      : ""
                  }
                  getOptionSelected={(opt, val) =>
                    isBlank(val) || opt.name === val.name
                  }
                  onChange={onRoleChange}
                  renderInput={params => {
                    params.inputProps["aria-autocomplete"] = "none";
                    params.inputProps["id"] = "role";
                    return (
                      <CustomizableTextField
                        required
                        params={params}
                        fieldOption="role"
                        onBlur={onBlur("role", path(["role"], fields))}
                        error={hasError(errors, "role")}
                        helperText={t(firstError(errors, "role"))}
                        name={"role-" + Math.random()}
                        defaultLabel={t("personal_details.role", "Role")}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default PersonalDetailsForm;
